import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const OperationalSettingsSection = ({ data, onChange, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState(data);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalData(data);
    setIsEditing(false);
    setIsSaving(false);
  }, [data]);

  const sportsOptions = [
    { value: 'pickleball', label: '피클볼' },
    { value: 'tennis', label: '테니스' },
    { value: 'badminton', label: '배드민턴' },
    { value: 'squash', label: '스쿼시' }
  ];

  const durationOptions = [
    { value: '60', label: '1시간' },
    { value: '90', label: '1시간 30분' },
    { value: '120', label: '2시간' },
    { value: '150', label: '2시간 30분' },
    { value: '180', label: '3시간' }
  ];

  const handleChange = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSportsChange = (value) => {
    setLocalData(prev => ({
      ...prev,
      availableSports: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(localData);
    setIsSaving(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalData(data);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-md flex items-center justify-center">
            <Icon name="Settings" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              운영 설정
            </h2>
            <p className="text-sm caption text-muted-foreground">
              시설의 운영 기본값을 설정합니다
            </p>
          </div>
        </div>
        
        {!isEditing && (
          <Button
            variant="outline"
            size="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            수정
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="기본 코트 수"
            type="number"
            placeholder="4"
            value={localData?.defaultCourts}
            onChange={(e) => handleChange('defaultCourts', e?.target?.value)}
            disabled={!isEditing}
            min="1"
            max="20"
            required
          />
          
          <Select
            label="기본 세션 시간"
            options={durationOptions}
            value={localData?.defaultDuration}
            onChange={(value) => handleChange('defaultDuration', value)}
            disabled={!isEditing}
            required
          />
        </div>

        <Select
          label="제공 스포츠"
          options={sportsOptions}
          value={localData?.availableSports}
          onChange={handleSportsChange}
          disabled={!isEditing}
          multiple
          searchable
          required
          description="시설에서 제공하는 스포츠 종목을 선택하세요"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="최소 참가자 수"
            type="number"
            placeholder="4"
            value={localData?.minParticipants}
            onChange={(e) => handleChange('minParticipants', e?.target?.value)}
            disabled={!isEditing}
            min="2"
            required
          />
          
          <Input
            label="최대 참가자 수"
            type="number"
            placeholder="16"
            value={localData?.maxParticipants}
            onChange={(e) => handleChange('maxParticipants', e?.target?.value)}
            disabled={!isEditing}
            min="2"
            required
          />
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground">
            시설 규칙
          </label>

          <Checkbox
            label="대기자 명단 활성화"
            description="정원 초과 시 대기자 명단을 운영합니다"
            checked={localData?.enableWaitlist}
            onChange={(e) => handleChange('enableWaitlist', e?.target?.checked)}
            disabled={!isEditing}
          />
        </div>

        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              fullWidth
            >
              취소
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
              fullWidth
            >
              저장
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationalSettingsSection;